import { dayjs } from '@/libs/dayjs';

import { DDD_MAP } from './ddd';
import { parseDigits } from './formatters';

/**
 * Normalizes a name string by applying Unicode normalization and collapsing multiple spaces.
 * @param input - The name string to normalize.
 * @returns The normalized name string.
 */
export const normalizeNameChars = (input: string): string => {
  if (!input) return '';
  return input
    .normalize('NFKC')
    .replace(/[’‘‛ʼ]/g, "'")
    .replace(/\s+/g, ' ');
};

/**
 * Validates whether the given input contains only valid characters for a name.
 * @param input - The name string to validate.
 * @returns `true` if the input contains only valid characters; otherwise, `false`.
 */
export const isValidNameChars = (input: string): boolean => {
  if (!input) return false;
  return !/[^\p{L}\s'.-]/u.test(normalizeNameChars(input));
};

/**
 * Mask helper for the social name input. Normalizes Unicode like
 * `normalizeNameChars` and strips dots and digits so the user cannot type them —
 * the schema still rejects them (via `addSocialNameValidationIssues`) as a
 * safety net for paste/autofill, but in normal typing they simply don't appear.
 */
export const normalizeSocialNameInput = (input: string): string =>
  normalizeNameChars(input).replace(/[.\d]/g, '');

/**
 * Validates whether the given input is a valid CPF number.
 *
 * @param input - The string to validate as CPF (with or without formatting).
 * @returns `true` if the input contains exactly 11 digits; otherwise, `false`.
 *
 * > Note: This does not validate CPF checksum digits.
 */
export const isValidCpf = (input: string): boolean => {
  const onlyDigits = parseDigits(input);
  return onlyDigits.length === 11;
};
/**
 * Validates whether the given input is a valid Brazilian phone number.
 *
 * @param input - The string to validate (with or without formatting).
 * @returns `true` if it matches common Brazilian mobile formats; otherwise, `false`.
 */
const VALID_DDDS = new Set(Object.keys(DDD_MAP).map(Number));

export const isValidPhone = (input: string): string | null => {
  const digits = input.replace(/\D/g, '');
  if (digits.length !== 11) return 'Telefone deve conter 11 dígitos';
  const ddd = Number(digits.slice(0, 2));
  if (!VALID_DDDS.has(ddd)) return 'DDD inválido';
  const phoneNumber = digits.slice(2);
  if (!/^9\d{8}$/.test(phoneNumber)) return 'Número de celular inválido';
  const subscriberDigits = phoneNumber.slice(1);
  if (/^(\d)\1{7}$/.test(subscriberDigits))
    return 'Número de telefone com sequência inválida';
  return null;
};

/**
 * Validates whether the given input is a properly formatted email address.
 *
 * @param input - The string to validate.
 * @returns `true` if the input has a local part, domain, and at least one "."; otherwise, `false`.
 */
export const isValidEmail = (input: string): boolean => {
  const trimmed = input.trim();
  if (trimmed.includes('*')) return false;
  // Regex that prevents acented characters, consecutive dots, dots at start/end, and other invalid patterns
  // [a-zA-Z0-9] does not match acented chars
  const emailRegex =
    /^[a-zA-Z0-9]+([._+%-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;
  // Explicitly check for accented characters to ensure none are present
  const accentedCharRegex = /[\u00C0-\u00FF]/;
  if (accentedCharRegex.test(trimmed)) return false;

  return emailRegex.test(trimmed);
};

/**
 * Validates whether the given input is either a valid email or CPF.
 *
 * @param input - The string to validate. It can be an email or CPF (with or without formatting).
 * @returns `true` if the input is a valid email or a CPF with exactly 11 digits; otherwise, `false`.
 */
export const isValidEmailOrCpf = (input: string): boolean => {
  return isValidEmail(input) || isValidCpf(input);
};

export const validateCPF = (cpf: string): string | null => {
  if (cpf.length !== 11) return 'CPF precisa ter ao menos 11 dígitos';
  if (/^(\d)\1{10}$/.test(cpf)) return 'CPF inválido';

  const calcDV = (base: number): number => {
    const sum = cpf
      .slice(0, base)
      .split('')
      .reduce((acc, n, idx) => acc + Number(n) * (base + 1 - idx), 0);
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };

  const dv1 = calcDV(9);
  const dv2 = calcDV(10);

  if (dv1 !== Number(cpf[9]) || dv2 !== Number(cpf[10])) return 'CPF inválido';
  return null;
};

export type PixKeyType = 'cpf' | 'phone' | 'email' | 'random';

const PIX_RANDOM_KEY_REGEX =
  /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

/**
 * Validates a Pix key against the format required by the selected key type.
 *
 * @param type - The Pix key type selected by the user.
 * @param rawKey - The key as typed (with or without formatting).
 * @returns `null` if valid; otherwise, a string error message.
 */
export const validatePixKey = (type: PixKeyType, rawKey: string): string | null => {
  const key = rawKey?.trim() ?? '';

  switch (type) {
    case 'cpf':
      return validateCPF(parseDigits(key));
    case 'phone':
      return isValidPhone(key);
    case 'email':
      return isValidEmail(key) ? null : 'Email inválido';
    case 'random':
      return PIX_RANDOM_KEY_REGEX.test(key)
        ? null
        : 'Chave aleatória inválida. Use o formato UUID';
    default:
      return null;
  }
};

/**
 * Validates whether a birth date string is correctly formatted and meets minimum age requirements.
 *
 * @param raw - The birth date string in the format 'DD / MM / AAAA'.
 * @returns `null` if valid; otherwise, a string error message.
 */
export const validateBirthDate = (raw?: string): string | null => {
  const input = raw?.trim() ?? '';

  const regex = /^\d{2} \/ \d{2} \/ \d{4}$/;
  if (!regex.test(input)) return 'Data inválida (use DD / MM / AAAA)';

  const clean = input.replace(/ /g, '').replace(/\//g, '');
  const day = clean.slice(0, 2);
  const month = clean.slice(2, 4);
  const year = clean.slice(4, 8);

  const birth = dayjs(`${year}-${month}-${day}`, 'YYYY-MM-DD', true);

  if (!birth.isValid()) return 'Data inválida';

  const age = dayjs().diff(birth, 'year');
  if (age < 16) return 'Você deve ter ao menos 16 anos';

  return null;
};
